import { useLiveQuery } from 'dexie-react-hooks'
import { BladApi, api } from './api.js'
import { db } from './db.js'
import type { Migawka, NazwaTabeli, WpisKolejki } from './db.js'
import type { Platnosc } from './typy.js'

export interface WynikWysylki {
  wyslane: number
  odrzucone: Array<{ wpis: WpisKolejki; powod: string }>
  /** Ile operacji zostało w kolejce, bo wysyłka utknęła na braku sieci. */
  zostalo: number
}

type Tabela = {
  get(id: string): Promise<unknown>
  put(rekord: unknown): Promise<unknown>
  delete(id: string): Promise<unknown>
}

function tabela(nazwa: NazwaTabeli): Tabela {
  return db[nazwa] as unknown as Tabela
}

/**
 * Wysyłka i pobieranie nie mogą się przeplatać: gdyby pobranie weszło w środek
 * wysyłki, przyniosłoby stan sprzed operacji i cofnęło to, co użytkownik już
 * widzi na ekranie. Jeden zamek na obie drogi rozwiązuje to bez kombinowania.
 */
let ogonek: Promise<unknown> = Promise.resolve()

export function zZamkiem<T>(zadanie: () => Promise<T>): Promise<T> {
  const wynik = ogonek.then(zadanie, zadanie)
  ogonek = wynik.catch(() => undefined)
  return wynik
}

let wyzwalaczSynchronizacji: (() => void) | null = null
let raportOdrzucen: ((wynik: WynikWysylki) => void) | null = null

/** Rejestruje próbę wysyłki uruchamianą po każdym zapisie (ustawia `sync.ts`). */
export function ustawWyzwalaczSynchronizacji(fn: (() => void) | null): void {
  wyzwalaczSynchronizacji = fn
}

/** Rejestruje sposób zgłaszania operacji odrzuconych przez serwer. */
export function ustawRaportOdrzucen(fn: ((wynik: WynikWysylki) => void) | null): void {
  raportOdrzucen = fn
}

export interface NowaOperacja {
  metoda: WpisKolejki['metoda']
  sciezka: string
  body?: unknown
  cofniecie: Migawka[]
  skutek: Migawka[]
  szkicPlatnosci?: string[]
}

/** Dopisuje operację do kolejki i od razu próbuje ją wysłać. */
export async function zakolejkuj(operacja: NowaOperacja): Promise<string> {
  const id = crypto.randomUUID()
  await db.outbox.put({
    id,
    metoda: operacja.metoda,
    sciezka: operacja.sciezka,
    body: operacja.body ?? null,
    cofniecie: operacja.cofniecie,
    skutek: operacja.skutek,
    szkicPlatnosci: operacja.szkicPlatnosci ?? [],
    utworzono: new Date().toISOString(),
    proby: 0,
    ostatniBlad: null,
  })
  // Zapis od razu próbuje wyjść do serwera; brak sieci zostawia go w kolejce.
  wyzwalaczSynchronizacji?.()
  return id
}

/**
 * Błąd trwały to taki, którego powtórzenie nie naprawi: odrzucona walidacja,
 * brak zasobu, konflikt. Wszystko inne (brak sieci, awaria serwera, wygasła
 * sesja) zostaje w kolejce do następnej próby.
 */
function trwaly(problem: unknown): boolean {
  if (!(problem instanceof BladApi)) return false
  if (problem.status === 401 || problem.status === 408 || problem.status === 429) return false
  return problem.status >= 400 && problem.status < 500
}

async function cofnij(wpis: WpisKolejki): Promise<void> {
  // Cofamy w odwrotnej kolejności, żeby zależne zapisy wróciły poprawnie.
  for (const krok of [...wpis.cofniecie].reverse()) {
    const cel = tabela(krok.tabela)
    if (krok.rekord === null) await cel.delete(krok.id)
    else await cel.put(krok.rekord)
  }
}

/** Pola, które niesie odhaczenie płatności — tylko one wracają przy nakładaniu. */
function stanZaplaty(platnosc: Platnosc) {
  return {
    status: platnosc.status,
    actualAmountMinor: platnosc.actualAmountMinor,
    actualCurrency: platnosc.actualCurrency,
    fxRate: platnosc.fxRate,
    amountPlnMinor: platnosc.amountPlnMinor,
    paidAt: platnosc.paidAt,
    notes: platnosc.notes,
    updatedAt: platnosc.updatedAt,
  }
}

async function nalozKrok(krok: Migawka): Promise<void> {
  const cel = tabela(krok.tabela)

  if (krok.rekord === null) {
    await cel.delete(krok.id)
    return
  }

  if (krok.tabela !== 'payments') {
    await cel.put(krok.rekord)
    return
  }

  // Płatność mogła w międzyczasie dostać serwerowy identyfikator — wtedy
  // rozpoznajemy ją po pozycji i terminie, dokładnie tak jak robi to API.
  const zamierzona = krok.rekord as Platnosc
  const istniejaca =
    (await db.payments.get(krok.id)) ??
    (await db.payments
      .where('[itemId+dueDate]')
      .equals([zamierzona.itemId, zamierzona.dueDate])
      .first())

  if (istniejaca === undefined) {
    await db.payments.put(zamierzona)
    return
  }
  await db.payments.put({ ...istniejaca, ...stanZaplaty(zamierzona) })
}

/**
 * Nakłada z powrotem wszystko, co czeka w kolejce. Wołane po każdym pobraniu
 * z serwera: serwer nie zna jeszcze tych zmian, więc jego wersja rekordu jest
 * z punktu widzenia użytkownika przestarzała.
 */
export async function nalozKolejke(): Promise<void> {
  const wpisy = await db.outbox.orderBy('utworzono').toArray()
  for (const wpis of wpisy) {
    for (const krok of wpis.skutek) await nalozKrok(krok)
  }
}

interface Rezultat {
  stan: 'wyslane' | 'odrzucone' | 'czekaj'
  powod: string
}

async function wyslijWpis(wpis: WpisKolejki): Promise<Rezultat> {
  try {
    const body = wpis.body === null ? undefined : wpis.body
    if (wpis.metoda === 'POST') await api.post(wpis.sciezka, body, wpis.id)
    else if (wpis.metoda === 'PATCH') await api.patch(wpis.sciezka, body, wpis.id)
    else await api.del(wpis.sciezka, wpis.id)
    return { stan: 'wyslane', powod: '' }
  } catch (problem) {
    const powod = problem instanceof Error ? problem.message : 'Nieznany błąd'
    if (trwaly(problem)) {
      await cofnij(wpis)
      return { stan: 'odrzucone', powod }
    }
    await db.outbox.put({ ...wpis, proby: wpis.proby + 1, ostatniBlad: powod })
    return { stan: 'czekaj', powod }
  }
}

/**
 * Wysyła kolejkę po kolei. Kolejność ma znaczenie — utworzenie pozycji musi
 * dotrzeć przed odhaczeniem jej płatności.
 */
export async function wyslijKolejke(): Promise<WynikWysylki> {
  return zZamkiem(async () => {
    const wpisy = await db.outbox.orderBy('utworzono').toArray()
    const wynik: WynikWysylki = { wyslane: 0, odrzucone: [], zostalo: 0 }

    for (const wpis of wpisy) {
      const rezultat = await wyslijWpis(wpis)

      if (rezultat.stan === 'czekaj') {
        // Pierwsza porażka przerywa wysyłkę: dalsze operacje mogą od tej zależeć.
        wynik.zostalo = wpisy.length - wpisy.indexOf(wpis)
        return wynik
      }

      await db.outbox.delete(wpis.id)
      if (rezultat.stan === 'wyslane') {
        wynik.wyslane += 1
        // Serwer wygenerował własny harmonogram; lokalny szkic musi ustąpić,
        // inaczej te same terminy pokazałyby się podwójnie.
        if (wpis.szkicPlatnosci.length > 0) await db.payments.bulkDelete(wpis.szkicPlatnosci)
        // Operacje, które wciąż czekają, mogły dotyczyć skasowanego szkicu —
        // nakładamy je ponownie, tym razem na wiersze od serwera.
        await nalozKolejke()
      } else {
        wynik.odrzucone.push({ wpis, powod: rezultat.powod })
      }
    }

    if (wynik.odrzucone.length > 0) raportOdrzucen?.(wynik)
    return wynik
  })
}

export function useLiczbaOczekujacych(): number {
  return useLiveQuery(() => db.outbox.count(), [], 0)
}
